import { AnimatePresence, motion } from "framer-motion";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Button } from "../common/Button";
import { LoadingState } from "../common/LoadingState";
import { ActorFleetGrouped } from "./ActorFleetGrouped";
import { FleetBentoHeader } from "./FleetBentoHeader";
import { InspectorSheet } from "./inspector/InspectorSheet";
import { useChannelInspectorState } from "./inspector/useChannelInspectorState";
import { pruneMaterializedSpawnIds } from "./instanceRealtime";
import { IssueWizardDialog } from "./issueWizard/IssueWizardDialog";
import { LifecycleChannelsSection } from "./LifecycleChannelsSection";
import { type SpawnInput, SpawnDialog } from "./SpawnDialog";
import { isSpawnFailure, useBlackboard } from "./useBlackboard";
import { useOrgChannels } from "./useOrgChannels";

export const BlackboardPage: FC = () => {
  const {
    actorAttributions,
    error,
    generatingInstances,
    instances,
    killInstance,
    loading,
    refresh,
    sendMessage,
    spawnInstance,
  } = useBlackboard({ includeKilledAttributions: false });
  const {
    byId: orgChannelsById,
    channels: orgChannels,
    loading: orgChannelsLoading,
    reload: reloadOrgChannels,
  } = useOrgChannels();
  const channelState = useChannelInspectorState();
  const [showSpawnDialog, setShowSpawnDialog] = useState(false);
  const [spawnChannelId, setSpawnChannelId] = useState<string | null>(null);
  const [showIssueWizard, setShowIssueWizard] = useState(false);
  const [spawning, setSpawning] = useState(false);
  const [recentlySpawnedInstanceIds, setRecentlySpawnedInstanceIds] = useState<
    ReadonlySet<string>
  >(new Set());
  // Spawn-local error: never reuse the hook's shared `error` (kill/fetch write
  // it too) — that would leak an unrelated error into the dialog and persist
  // past a later success. Cleared on dialog-open and on each spawn attempt.
  const [spawnError, setSpawnError] = useState<string | null>(null);

  // The Inspector is a two-slot stack driven by the URL: a BASE entity
  // (`?inspect=actor:<id>` or `?inspect=channel:<id>`) and an optional
  // channel OVERLAY stacked above an actor base (`&overlay=channel:<id>`).
  // Opening a channel from an actor (pivot / Workspace link) sets the overlay
  // — the actor base stays mounted underneath with all its state intact, so
  // closing the overlay returns to the actor with no re-mode and no reset.
  // `?actor=<id>` is kept as a read-only alias for cross-page deep links
  // (Templates lineage), normalized to an actor inspect. `replace: true` on
  // all URL writes so browser-back closes the sheet instead of traversing
  // in-sheet hops.
  const [searchParams, setSearchParams] = useSearchParams();
  const inspectParam = searchParams.get("inspect");
  const overlayParam = searchParams.get("overlay");
  const legacyActorId = searchParams.get("actor");
  const inspectChannelId = inspectParam?.startsWith("channel:")
    ? inspectParam.slice("channel:".length)
    : null;
  const inspectActorId = inspectParam?.startsWith("actor:")
    ? inspectParam.slice("actor:".length)
    : legacyActorId;
  const overlayChannelId = overlayParam?.startsWith("channel:")
    ? overlayParam.slice("channel:".length)
    : null;
  // An actor drilled into from a channel's presence rail stacks ABOVE the
  // channel base: you see the actor, ‹ Back pops to the channel intact.
  const overlayActorId = overlayParam?.startsWith("actor:")
    ? overlayParam.slice("actor:".length)
    : null;

  const writeInspect = useCallback(
    (token: string | null, overlay: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("actor");
          next.delete("tab");
          next.delete("workspaceChannel");
          if (token) {
            next.set("inspect", token);
          } else {
            next.delete("inspect");
          }
          if (overlay) {
            next.set("overlay", overlay);
          } else {
            next.delete("overlay");
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Open an actor as the base focus. If a channel overlay is already open,
  // keep it (the actor swaps underneath its context). If the base was a
  // channel (opened from the fleet header), promote that channel to the
  // overlay so the member you just clicked stays as context.
  // Show the actor NOW, and give the way back. From a channel context (base
  // channel, or a channel overlay above another actor) the actor stacks as
  // the OVERLAY above that channel — visible immediately, ‹ Back pops to the
  // channel with its state intact. (The inverse — promoting the channel to
  // the overlay — left the channel sheet covering the actor: a member click
  // visibly did nothing.) With no channel context (fleet card click), the
  // actor is simply the base.
  const inspectActor = useCallback(
    (instanceId: string) => {
      const channelContext = overlayChannelId ?? inspectChannelId;
      if (channelContext !== null) {
        writeInspect(`channel:${channelContext}`, `actor:${instanceId}`);
        return;
      }
      writeInspect(`actor:${instanceId}`, null);
    },
    [overlayChannelId, inspectChannelId, writeInspect],
  );
  // Open a channel. From an actor base, stack it as the overlay (base stays).
  // From a channel base or no base, make it the base.
  const inspectChannel = useCallback(
    (channelId: string) => {
      if (inspectActorId !== null) {
        writeInspect(`actor:${inspectActorId}`, `channel:${channelId}`);
        return;
      }
      writeInspect(`channel:${channelId}`, null);
    },
    [inspectActorId, writeInspect],
  );
  const closeOverlay = useCallback(() => {
    writeInspect(
      inspectActorId !== null ? `actor:${inspectActorId}` : inspectParam,
      null,
    );
  }, [inspectActorId, inspectParam, writeInspect]);
  const closeInspect = useCallback(() => {
    writeInspect(null, null);
  }, [writeInspect]);
  // The linear inspect-stack + breadcrumb is gone — the overlay slot is the
  // stack now (depth ≤ 2: base + optional channel overlay). The overlay's
  // ‹ Back button (Esc / backdrop / close) is the single back affordance.
  const jumpInspect = useCallback(() => {
    /* No-op: retained for the InspectorSheet prop shape */
  }, []);

  // Highlight pulse for instances arrived-at via cross-page navigation
  // (e.g. clicking an actor avatar on the Templates page lineage row).
  // The router carries `?highlight=<id>` in the search params; we copy it
  // Into transient local state, scroll the card into view, then strip the
  // Param + clear local state after the bounce + shimmer animation
  // Finishes (~1.5s). Search params survive re-renders that location.state
  // Doesn't, so this approach is robust against router quirks.
  const highlightParam = searchParams.get("highlight");
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  useEffect(() => {
    if (!highlightParam) {
      return;
    }
    setHighlightedId(highlightParam);
    requestAnimationFrame(() => {
      const el = cardRefs.current.get(highlightParam);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    const timer = setTimeout(() => {
      setHighlightedId(null);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("highlight");
          return next;
        },
        { replace: true },
      );
    }, 1000);
    return () => clearTimeout(timer);
  }, [highlightParam, setSearchParams]);

  // Auto-focus expansion removed: tying focus to `generatingInstances`
  // caused the card to expand/contract on every >3s token-gap (normal agentic
  // pause — thinking, tool calls), because GENERATING_TTL_MS=3000 would drop
  // the instance from the generating set, nulling focus and unmounting the
  // FocusLiveRow mid-run. The "generating" status is already conveyed by the
  // status dot + accent bar; the timeline/expansion was redundant and the
  // bounce was a glitch. Keep the prop plumbing (downstream still accepts a
  // focusInstanceId) but never select one — all cards stay compact.
  const focusInstanceId = null;

  useEffect(() => {
    setRecentlySpawnedInstanceIds((previous) =>
      pruneMaterializedSpawnIds(previous, instances),
    );
  }, [instances]);

  const handleSpawn = useCallback(
    async (input: SpawnInput) => {
      setSpawning(true);
      // Clear prior error so a retry starts clean and a success leaves none.
      setSpawnError(null);
      try {
        const result = await spawnInstance(input);
        if (isSpawnFailure(result)) {
          // Render the backend's own reason inline (a 409 template conflict
          // says which template already occupies the channel). The global
          // banner is occluded by the Modal overlay, so spawn failures never
          // populate it.
          setSpawnError(result.message);
          return;
        }
        setRecentlySpawnedInstanceIds((previous) => {
          if (previous.has(result.id)) {
            return previous;
          }
          const next = new Set(previous);
          next.add(result.id);
          return next;
        });
        setShowSpawnDialog(false);
        inspectActor(result.id);
      } finally {
        setSpawning(false);
      }
    },
    [spawnInstance, inspectActor],
  );
  const openSpawnDialog = useCallback(() => {
    setSpawnError(null);
    setSpawnChannelId(null);
    setShowSpawnDialog(true);
  }, []);
  const openSpawnDialogForChannel = useCallback((channelId: string) => {
    setSpawnError(null);
    setSpawnChannelId(channelId);
    setShowSpawnDialog(true);
  }, []);
  const handleCardRef = useCallback(
    (instanceId: string, element: HTMLDivElement | null) => {
      if (element) {
        cardRefs.current.set(instanceId, element);
      } else {
        cardRefs.current.delete(instanceId);
      }
    },
    [],
  );

  if (loading && instances.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center px-3 lg:px-6">
        <LoadingState className="max-w-3xl" rows={4} rowHeight="h-20" />
      </div>
    );
  }

  const ambientPulse = generatingInstances.size > 0;

  return (
    <motion.div
      className="relative flex flex-1 flex-col px-3 lg:px-6"
      style={{
        backgroundImage:
          "linear-gradient(to right, #80808008 1px, transparent 1px), linear-gradient(to bottom, #80808008 1px, transparent 1px)",
        backgroundSize: "32px 32px",
      }}
      animate={{ opacity: 1 }}
    >
      {/* Ambient radial pulse — active while any actor is generating. */}
      <AnimatePresence>
        {ambientPulse && (
          <motion.div
            key="ambient-pulse"
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(ellipse at 50% 40%, rgba(16,185,129,0.06), transparent 65%)",
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.5, 1, 0.5] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 3.2, ease: "easeInOut", repeat: Infinity }}
          />
        )}
      </AnimatePresence>

      {/* Top bar with spawn control */}
      <div className="relative z-10 flex items-center justify-between py-3">
        <h2 className="font-mono text-[11px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
          {instances.length} {instances.length === 1 ? "instance" : "instances"}
        </h2>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowIssueWizard(true)}
          >
            From Jira
          </Button>
          <Button size="sm" onClick={openSpawnDialog}>
            Spawn Agent
          </Button>
        </div>
      </div>

      {instances.length > 0 && (
        <div className="relative z-10">
          <FleetBentoHeader
            instances={instances}
            generatingInstances={generatingInstances}
          />
        </div>
      )}

      {error && (
        <div className="relative z-10 mb-3 border border-rose-200 bg-rose-50 px-4 py-2 font-mono text-xs text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300">
          {error}
        </div>
      )}

      <LifecycleChannelsSection
        channels={orgChannels}
        onInspectChannel={inspectChannel}
        onAddActor={openSpawnDialogForChannel}
      />

      {/* Instance grid */}
      {instances.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-6 py-16">
          <div className="flex items-center gap-2 font-mono text-3xl font-bold text-slate-600/40 dark:text-slate-600">
            <span>[</span>
            <span className="text-emerald-500/30 dark:text-emerald-500/20">
              —
            </span>
            <span>]</span>
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              No agents running
            </p>
            <p className="mt-1 font-mono text-[11px] tracking-tight text-slate-400 dark:text-slate-500">
              Spawn your first persistent actor instance to get started.
            </p>
          </div>
          <Button onClick={openSpawnDialog}>Spawn Agent</Button>
        </div>
      ) : (
        <ActorFleetGrouped
          focusInstanceId={focusInstanceId}
          generatingInstances={generatingInstances}
          highlightedId={highlightedId}
          instances={instances}
          inspectedChannelId={inspectChannelId ?? overlayChannelId}
          inspectedInstanceId={inspectActorId}
          onCardRef={handleCardRef}
          onSelectInstance={inspectActor}
          onInspectChannel={inspectChannel}
          orgChannelsById={orgChannelsById}
        />
      )}

      {/* Jira ticket → workspace wizard */}
      {showIssueWizard && (
        <IssueWizardDialog
          onClose={() => setShowIssueWizard(false)}
          onOpenChannel={inspectChannel}
        />
      )}

      {/* Spawn dialog */}
      {showSpawnDialog && (
        <SpawnDialog
          actionError={spawnError}
          initialSharedChannelId={spawnChannelId}
          onClose={() => {
            setShowSpawnDialog(false);
            setSpawnChannelId(null);
          }}
          onSpawn={handleSpawn}
          spawning={spawning}
        />
      )}

      {/* Unified Inspector — a two-slot stack: a base entity (actor or
          channel) plus an optional channel overlay stacked above an actor
          base. Opens in place over the Fleet from `?inspect=actor:<id>`
          (card click), `?inspect=channel:<id>` (group header), or
          `&overlay=channel:<id>` (actor pivot / Workspace link). */}
      <InspectorSheet
        actorAttributions={actorAttributions}
        isOpen={Boolean(inspectChannelId || inspectActorId)}
        channelId={inspectChannelId}
        actorId={inspectActorId}
        overlayChannelId={overlayChannelId}
        onClose={closeInspect}
        onCloseOverlay={closeOverlay}
        ancestors={[]}
        onJumpTo={jumpInspect}
        onInspectChannel={inspectChannel}
        onInspectActor={inspectActor}
        overlayActorId={overlayActorId}
        instances={instances}
        orgChannelsById={orgChannelsById}
        orgChannelsLoading={orgChannelsLoading}
        onReloadOrgChannels={reloadOrgChannels}
        recentlySpawnedInstanceIds={recentlySpawnedInstanceIds}
        generatingInstances={generatingInstances}
        sendMessage={sendMessage}
        onKillInstance={killInstance}
        onRefresh={refresh}
        channelState={channelState}
      />
    </motion.div>
  );
};
